import React, { useContext, useEffect, useMemo, useState } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ExternalUserType,
	type InvitedGroup,
	type InvitedTeam,
	type InvitedUser,
	type InviteeProvider,
	isUserWithEmail,
} from '../../types';
import { type User, type OptionData, type ExternalUser } from '@atlaskit/user-picker';
import { type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { type OptionProps as ReactSelectOptionProps } from '@atlaskit/select';
import { isEmail, isUser, isExternalUser } from '@atlaskit/user-picker';
import { InviteeListAnalytics } from './InviteeListAnalytics';
import { useAnalytics } from '../../analytics';

export type OptionType = OptionData | User | ExternalUser;

export type InviteeListState = {
	allowEmail: boolean;
	// userPickerOptions is used for userPicker state.
	userPickerOptions: OptionType[];
	setUserPickerOptions: React.Dispatch<React.SetStateAction<OptionType[]>>;
	// derived from userPickerOptions
	emails: string[];
	// derived from userPickerOptions
	ids: string[];
	// derived from userPickerOptions
	users: InvitedUser[];
	groups: InvitedGroup[];
	teams: InvitedTeam[];
	trailingText: string;
	setTrailingText: React.Dispatch<React.SetStateAction<string>>;
	getName: (id: string) => string;
	getUser: (id: string) => OptionType | undefined;
	getExternalUserType: (user: InvitedUser) => ExternalUserType | undefined;
	experimental_userPickerByline?: string;
	inviteeListLabelText?: React.ReactNode;
	inviteeListPlaceholderText?: React.ReactNode;
	inviteeListRequired?: boolean;
	groupByTypeOrder?: NonNullable<OptionData['type']>[];
	renderOptionWithProfileCard?: (
		optionProps: ReactSelectOptionProps<OptionData, boolean>,
		optionData: OptionData,
		children: React.ReactNode,
	) => React.ReactNode;
};

export const InviteeListStateContext = React.createContext<InviteeListState | undefined>(undefined);

export interface InviteeListContextProps {
	allowEmail?: boolean;
	children: React.ReactElement;
	initialInvitees?: OptionType[];
	experimental_userPickerByline?: string;
	inviteeListLabelText?: React.ReactNode;
	inviteeListPlaceholderText?: React.ReactNode;
	inviteeListRequired?: boolean;
	groupByTypeOrder?: NonNullable<OptionData['type']>[];
	inviteeProvider?: InviteeProvider;
}

export const getEmailsAndIds = (
	options: OptionType[],
	includeGroupsInIds: boolean,
): InvitedUser[] =>
	options
		.filter((option) => includeGroupsInIds || (option.type !== 'group' && option.type !== 'team'))
		.map((option) => {
			if (isEmail(option)) {
				const nameOrId = option.name || option.id;
				return { email: nameOrId };
			}

			if (isUser(option) || isExternalUser(option)) {
				if (option.email) {
					return { email: option.email };
				} else {
					const id = option.id;
					return { id };
				}
			}
			// Use the id if option is not of type USER or EMAIL
			const id = option.id;
			return { id };
		});

export const getGroupIds = (options: OptionType[]): InvitedGroup[] =>
	options.filter((option) => option.type === 'group').map(({ id }) => ({ id }));

export const getTeamsIds = (options: OptionType[]): InvitedTeam[] =>
	options.filter((option) => option.type === 'team').map(({ id }) => ({ id }));

export const getEmailAndIdArrays = (options: OptionType[]): { emails: string[]; ids: string[] } => {
	const emailsAndIds = getEmailsAndIds(options, true);
	return {
		emails: emailsAndIds.flatMap((item) =>
			isUserWithEmail(item) && item.email ? [item.email] : [],
		),
		// only get ID if email does not exist and ID exists.
		ids: emailsAndIds.flatMap((item) => (item.id && !isUserWithEmail(item) ? [item.id] : [])),
	};
};

export const getExternalUserTypeFromOptions = (options: OptionType[], invitedUser: InvitedUser) => {
	const user = options.find((option) => {
		if (isExternalUser(option)) {
			return option.email === invitedUser.email || option.id === invitedUser.id;
		}
		return false;
	});
	return user ? (user as ExternalUser).externalUserType : undefined;
};

export const InviteeListContext = ({
	allowEmail,
	children,
	initialInvitees,
	experimental_userPickerByline,
	inviteeListLabelText,
	inviteeListPlaceholderText,
	inviteeListRequired,
	groupByTypeOrder,
	inviteeProvider,
}: InviteeListContextProps & WithAnalyticsEventsProps): React.JSX.Element => {
	const analytics = useAnalytics(InviteeListAnalytics);
	const [userPickerOptions, setUserPickerOptions] = useState<OptionType[]>(
		() => initialInvitees ?? [],
	);
	const [trailingText, setTrailingText] = React.useState('');

	useEffect(() => {
		if (initialInvitees && initialInvitees.length > 0) {
			analytics.inviteeListPrefilled(initialInvitees.length);
		}
	}, [initialInvitees, analytics]);

	// emails ands ids that are in the user picker. This array will be used when inviting.
	const users = getEmailsAndIds(userPickerOptions, false);
	const groups = getGroupIds(userPickerOptions);
	const teams = useMemo(
		() => (fg('make-invite-people-modal-support-teams') ? getTeamsIds(userPickerOptions) : []),
		[userPickerOptions],
	);
	// emails and ids as string arrays.
	const { emails, ids } = getEmailAndIdArrays(userPickerOptions);

	const contextValue = useMemo<InviteeListState>(() => {
		const getName = (id: string) => {
			return userPickerOptions.find((item) => item.id === id)?.name || id;
		};
		const getUser = (id: string) => userPickerOptions.find((user) => user.id === id);
		const getExternalUserType = (user: InvitedUser) =>
			getExternalUserTypeFromOptions(userPickerOptions, user);

		return {
			// NOTE: default value of `allowEmail` is `true` given backwards compatibility,
			//       since UserPicker's `allowEmail` was always `true` before the Invite People `allowEmail` prop
			allowEmail: allowEmail ?? true,
			userPickerOptions,
			setUserPickerOptions,
			trailingText,
			setTrailingText,
			users,
			groups,
			teams,
			emails,
			ids,
			getName,
			getUser,
			getExternalUserType,
			experimental_userPickerByline,
			inviteeListLabelText,
			inviteeListPlaceholderText,
			inviteeListRequired,
			groupByTypeOrder,
			renderOptionWithProfileCard: inviteeProvider?.renderOptionWithProfileCard,
		};
	}, [
		allowEmail,
		userPickerOptions,
		trailingText,
		users,
		groups,
		teams,
		emails,
		ids,
		experimental_userPickerByline,
		inviteeListLabelText,
		inviteeListPlaceholderText,
		inviteeListRequired,
		groupByTypeOrder,
		inviteeProvider,
	]);

	return (
		<InviteeListStateContext.Provider value={contextValue}>
			{children}
		</InviteeListStateContext.Provider>
	);
};

export const useInviteeListStateContext = () => {
	const state = useContext(InviteeListStateContext);

	if (state === undefined) {
		throw new Error(
			'useInviteeListStateContext must be used within a InviteeListStateContextProvider',
		);
	}

	return state;
};
